import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';

export type AppToastVariant = 'error' | 'warning' | 'success' | 'info';

export interface AppToastData {
	variant: AppToastVariant;
	title: string;
	message: string;
	/** Detalhe técnico do erro (método, rota e status HTTP), em letra menor. */
	detail?: string | null;
	/** Rótulo do botão; sem rótulo o botão não aparece. */
	actionLabel?: string | null;
	/** A chamada original tinha ação: o botão dispara `onAction()` para quem assina. */
	hasAction: boolean;
	/** Rótulo acessível do X, no idioma da tela. */
	closeLabel: string;
}

const ICONS: Record<AppToastVariant, string> = {
	error: 'heroicons_outline:exclamation-circle',
	warning: 'heroicons_outline:exclamation',
	success: 'heroicons_outline:check-circle',
	info: 'heroicons_outline:information-circle',
};

/**
 * Aviso flutuante do sistema, no canto inferior direito.
 *
 * Substitui a faixa padrão do MatSnackBar para todas as telas (ver
 * `install-app-toast.ts`): título com ícone, botão de fechar, a mensagem e o
 * botão de confirmação. Em erro, mostra o motivo que o backend devolveu.
 */
@Component({
	selector: 'app-toast',
	standalone: true,
	imports: [CommonModule, MatIconModule],
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		<div class="app-toast" [class]="'app-toast--' + data.variant" role="alert" aria-live="assertive">
			<div class="app-toast__header">
				<mat-icon class="app-toast__icon" [svgIcon]="icon"></mat-icon>
				<span class="app-toast__title">{{ data.title }}</span>
				<button type="button" class="app-toast__close" [attr.aria-label]="data.closeLabel" (click)="close()">
					<mat-icon svgIcon="heroicons_outline:x"></mat-icon>
				</button>
			</div>
			<p class="app-toast__message">{{ data.message }}</p>
			<p *ngIf="data.detail" class="app-toast__detail">{{ data.detail }}</p>
			<button *ngIf="data.actionLabel" type="button" class="app-toast__action" (click)="confirm()">
				{{ data.actionLabel }}
			</button>
		</div>
	`,
	styles: [`
		:host { display: block; }

		/* Cores do tema do sistema (variáveis do Fuse): segue claro/escuro sozinho. */
		.app-toast {
			--toast-accent: rgb(var(--fuse-primary-500-rgb));

			width: 380px;
			max-width: calc(100vw - 32px);
			padding: 16px 16px 16px 18px;
			border-radius: 12px;
			border: 1px solid rgba(var(--fuse-border-rgb), 0.12);
			background: rgb(var(--fuse-bg-card-rgb));
			color: rgb(var(--fuse-text-default-rgb));
			box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
			font-size: 14px;
			line-height: 1.45;
		}

		.app-toast--error { --toast-accent: rgb(var(--fuse-warn-500-rgb)); }
		.app-toast--warning { --toast-accent: #d97706; }
		.app-toast--success { --toast-accent: #16a34a; }

		.app-toast__header {
			display: flex;
			align-items: center;
			gap: 10px;
		}

		.app-toast__icon {
			flex: none;
			width: 20px;
			height: 20px;
			min-width: 20px;
			min-height: 20px;
			color: var(--toast-accent) !important;
		}

		.app-toast__title {
			flex: 1 1 auto;
			min-width: 0;
			font-size: 16px;
			font-weight: 600;
		}

		.app-toast__close {
			flex: none;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: 28px;
			height: 28px;
			margin: -4px -4px -4px 0;
			border-radius: 6px;
			color: rgb(var(--fuse-text-secondary-rgb));
			cursor: pointer;
		}
		.app-toast__close:hover { background: var(--fuse-bg-hover); }
		.app-toast__close mat-icon {
			width: 20px; height: 20px; min-width: 20px; min-height: 20px;
			color: inherit !important;
		}

		.app-toast__message {
			margin: 10px 0 0;
			color: rgb(var(--fuse-text-secondary-rgb));
			white-space: pre-line;
			overflow-wrap: anywhere;
		}

		.app-toast__detail {
			margin: 6px 0 0;
			font-size: 12px;
			color: rgb(var(--fuse-text-hint-rgb));
			font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
			overflow-wrap: anywhere;
		}

		.app-toast__action {
			margin-top: 14px;
			padding: 8px 16px;
			border-radius: 8px;
			background: rgb(var(--fuse-primary-500-rgb));
			color: #ffffff;
			font-weight: 500;
			cursor: pointer;
		}
		.app-toast__action:hover { filter: brightness(0.92); }
	`],
})
export class AppToastComponent {
	constructor(
		@Inject(MAT_SNACK_BAR_DATA) public readonly data: AppToastData,
		private readonly snackBarRef: MatSnackBarRef<AppToastComponent>,
	) { }

	get icon(): string {
		return ICONS[this.data.variant];
	}

	close(): void {
		this.snackBarRef.dismiss();
	}

	confirm(): void {
		if (this.data.hasAction) {
			this.snackBarRef.dismissWithAction();
		} else {
			this.snackBarRef.dismiss();
		}
	}
}
